import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, type RouterHistory } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Toaster } from 'sonner';

import { setUnauthorisedHandler } from './api/client';
import { authKeys } from './auth/hooks';
import { useAuthStore } from './auth/store';
import { buildRouter } from './router';

export interface AppProps {
  queryClient?: QueryClient;
  history?: RouterHistory;
}

const defaultQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000, retry: false },
    },
  });

export const App = ({ queryClient, history }: AppProps) => {
  // Keep QueryClient and Router stable across re-renders; tests pass their own
  // instances so each render gets a clean slate.
  const [qc] = useState(() => queryClient ?? defaultQueryClient());
  const [router] = useState(() => buildRouter(qc, history));

  // Plan 11-01 (BUG-003): wire the api-client 401 hook to clear auth-state
  // and route the user to /login. Late-bound so `api/client` stays free of
  // router/store imports. Cleanup nulls the handler on unmount so test
  // teardown doesn't leak between cases.
  useEffect(() => {
    setUnauthorisedHandler(() => {
      useAuthStore.getState().clear();
      qc.setQueryData(authKeys.me, null);
      void router.navigate({ to: '/login' });
    });
    return () => {
      setUnauthorisedHandler(null);
    };
  }, [qc, router]);

  return (
    <QueryClientProvider client={qc}>
      <RouterProvider router={router} />
      <Toaster richColors closeButton position="top-right" theme="dark" />
    </QueryClientProvider>
  );
};
